import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((index: number) => {
    setActive((index + movies.length) % movies.length);
  }, [movies.length]);

  useEffect(() => {
    if (paused || movies.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % movies.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, movies.length]);

  if (movies.length === 0) {
    return (
      <section className="w-full h-[420px] md:h-[560px] bg-background-100 flex items-center justify-center">
        <p className="text-foreground-600 text-sm">暂无热门推荐，敬请期待</p>
      </section>
    );
  }

  return (
    <section
      className="relative w-full h-[520px] md:h-[620px] overflow-hidden bg-background-200"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {movies.map((movie, index) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title} 剧照`}
            title={`${movie.title} - 嫩草影院高清在线看`}
            className={`w-full h-full object-cover object-top ${
              index === active ? "animate-slow-zoom" : ""
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-50/95 via-foreground-50/70 to-foreground-50/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/20 to-transparent" />
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
        <div className="max-w-2xl pt-16">
          {movies.map((movie, index) => (
            <div
              key={movie.id}
              className={index === active ? "block" : "hidden"}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-1 rounded-sm bg-primary-500 text-white text-[11px] font-medium tracking-wide">
                  {movie.featured ? "本周焦点" : "编辑推荐"}
                </span>
                <span className="px-2.5 py-1 rounded-sm bg-background-50/60 backdrop-blur-sm border border-background-400/70 text-foreground-800 text-[11px]">
                  {movie.type === "movie" ? "电影" : "剧集"} · {movie.year}
                </span>
                <span className="flex items-center gap-1 text-accent-500 text-xs font-semibold">
                  <i className="ri-star-fill" />
                  {movie.rating.toFixed(1)}
                </span>
              </div>
              <h1 className="mt-4 text-3xl md:text-5xl font-black tracking-tight text-foreground-950 leading-tight">
                {movie.title}
              </h1>
              <p className="mt-1.5 text-xs md:text-sm tracking-[0.2em] text-foreground-500 uppercase">
                {movie.englishTitle}
              </p>
              <p className="mt-4 text-sm md:text-[15px] leading-7 text-foreground-700 max-w-xl line-clamp-3">
                {movie.synopsis}
              </p>
              <p className="mt-4 text-xs text-foreground-500">
                {movie.genres.join(" / ")} · {movie.region} · {movie.duration} · 导演 {movie.director}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${movie.id}`}
                  className="h-11 px-6 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors whitespace-nowrap"
                >
                  <i className="ri-file-info-line text-base" />
                  查看详情
                </Link>
                <Link
                  to={`/category?type=${movie.type}&genre=${encodeURIComponent(movie.genres[0])}`}
                  className="h-11 px-6 inline-flex items-center gap-2 rounded-md bg-background-50/70 hover:bg-background-100 backdrop-blur-sm border border-background-400 text-foreground-900 text-sm font-medium transition-colors whitespace-nowrap"
                >
                  <i className="ri-grid-line text-base" />
                  同类型影片
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => goTo(active - 1)}
        className="absolute z-20 left-3 md:left-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-foreground-50/50 hover:bg-primary-500 backdrop-blur-sm border border-background-500/50 hidden md:flex items-center justify-center text-white transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => goTo(active + 1)}
        className="absolute z-20 right-3 md:right-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-foreground-50/50 hover:bg-primary-500 backdrop-blur-sm border border-background-500/50 hidden md:flex items-center justify-center text-white transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      <div className="absolute z-20 bottom-6 left-4 md:left-8 flex items-center gap-2">
        {movies.map((movie, index) => (
          <button
            key={movie.id}
            type="button"
            aria-label={`切换到 ${movie.title}`}
            onClick={() => goTo(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === active ? "w-8 bg-primary-500" : "w-4 bg-foreground-700/50 hover:bg-foreground-700"
            }`}
          />
        ))}
        <span className="ml-3 text-[11px] tracking-widest text-foreground-500">
          {String(active + 1).padStart(2, "0")} / {String(movies.length).padStart(2, "0")}
        </span>
      </div>
    </section>
  );
}